import React, { useEffect, useState } from 'react';
import { 
  Button, 
  Card, 
  Row, 
  Col, 
  Input, 
  Select, 
  DatePicker, 
  Space, 
  message,
  Tooltip,
  Modal,
  Alert,
  Typography,
  Segmented,
} from 'antd';
import { 
  SearchOutlined, 
  EditOutlined, 
  DeleteOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { useGetApplicationsQuery, useDeleteApplicationMutation, useGetClientsForFilterQuery } from '../../../store/api/applicationsApi';
import { useGetUsersQuery } from '../../../store/api/usersApi';
import { Application, ApplicationDateField, ApplicationListScope, ApplicationStatus } from '../../../types';
import { ApplicationCell, DataTable, ExternalApplicationCell, MoneyCell, StatusCell } from '../../../ui';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { applicationRouteDirectionLabel } from '../../../utils/addressUtils';
import {
  filterResponsibleEligibleUsers,
  formatResponsibleUserLabel,
} from '../../../utils/userDisplayName';
import { formatWorkspaceCarrierAgreementLine } from '../../workspace/WorkspaceTasks';
import './ApplicationList.css';

// Title intentionally unused here
const { Option } = Select;
const { RangePicker } = DatePicker;
const { Text } = Typography;
const APPLICATIONS_TABLE_SCROLL = { x: 'max-content' as const, y: 560 };

interface ApplicationListProps {
  onCreateClick: () => void;
  onViewClick: (application: Application) => void;
  onEditClick: (application: Application) => void;
  onUploadClick?: (application: Application) => void;
  applicationUpdatedTrigger?: number;
}

const ApplicationList: React.FC<ApplicationListProps> = ({
  onCreateClick,
  onViewClick,
  onEditClick,
  onUploadClick,
  applicationUpdatedTrigger
}) => {
  const [listScope, setListScope] = useState<ApplicationListScope>('active');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    clientId: '' as string,
    responsibleLogistId: '' as string,
    status: '' as string,
    direction: '' as string,
    dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null,
    dateField: 'applicationDate' as ApplicationDateField,
    source: '' as 'internal' | 'external' | '',
  });

  const [deleteApplication] = useDeleteApplicationMutation();

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, refetch, error } = useGetApplicationsQuery({
    page: filters.page,
    limit: filters.limit,
    scope: listScope,
    search: debouncedSearch || undefined,
    clientId: filters.clientId || undefined,
    responsibleLogistId: filters.responsibleLogistId || undefined,
    status: filters.status || undefined,
    direction: filters.direction || undefined,
    dateFrom: filters.dateRange?.[0]?.format('YYYY-MM-DD'),
    dateTo: filters.dateRange?.[1]?.format('YYYY-MM-DD'),
    dateField: filters.dateField,
    source: filters.source || undefined,
  });

  // Эффект для обновления данных при изменении заявки
  React.useEffect(() => {
    if (applicationUpdatedTrigger !== undefined && applicationUpdatedTrigger > 0) {
      const timeoutId = setTimeout(() => {
        refetch();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [applicationUpdatedTrigger, refetch]);

  const { data: clientsData } = useGetClientsForFilterQuery();
  const { data: usersData } = useGetUsersQuery();

  // Если есть ошибка загрузки
  if (error) {
    console.error('❌ Applications API error:', error);
    
    const errorMessage = error && typeof error === 'object' && 'message' in error 
      ? error.message 
      : JSON.stringify(error);
    
    return (
      <div className="application-list">
        <Card>
          <Alert
            message="Ошибка загрузки заявок"
            description={`Не удалось загрузить список заявок. ${errorMessage}`}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => refetch()}>
                Повторить
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  const canCancelApplication = (application: Application) => application.status !== 'CANCELLED';

  const handleDelete = (application: Application) => {
    if (!canCancelApplication(application)) {
      message.warning('Заявка уже отменена');
      return;
    }
    Modal.confirm({
      title: 'Отмена заявки',
      content: `Отменить заявку ${application.internalNumber}? Маршруты и связанные финансовые объекты будут помечены как отменённые, данные сохранятся.`,
      okText: 'Отменить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await deleteApplication(application.id).unwrap();
          message.success('Заявка отменена');
          refetch();
        } catch (error: any) {
          message.error(error?.data?.error || 'Ошибка отмены заявки');
        }
      }
    });
  };

  const handleFiltersChange = (key: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value,
    }));
  };

  const resetFilters = () => {
    setSearchInput('');
    setDebouncedSearch('');
    setFilters({
      page: 1,
      limit: 10,
      clientId: '',
      responsibleLogistId: '',
      status: '',
      direction: '',
      dateRange: null,
      dateField: 'applicationDate',
      source: '' as any,
    });
  };

  const dateFieldLabels: Record<ApplicationDateField, string> = {
    applicationDate: 'Дата заявки',
    plannedLoadingDate: 'План погрузки',
    plannedUnloadingDate: 'План выгрузки',
    actualLoadingDate: 'Факт погрузки',
    actualUnloadingDate: 'Факт выгрузки',
  };

  // Общий компонент таблицы для избежания дублирования
  const renderTable = () => (
    <DataTable
      columns={columns}
      dataSource={data?.applications || []}
      rowKey="id"
      loading={isLoading}
      rowClassName={() => 'application-list-row-clickable'}
      onRow={(record) => ({
        onClick: (e) => {
          const target = e.target as HTMLElement;
          // Не реагировать на клики по интерактивным элементам в колонке действий
          if (target.closest('button') || target.closest('a')) return;
          onViewClick(record as Application);
        }
      })}
      scroll={APPLICATIONS_TABLE_SCROLL}
      pagination={{
        current: filters.page,
        pageSize: filters.limit,
        total: data?.pagination?.total || 0,
        showSizeChanger: true,
        showQuickJumper: true,
        showTotal: (total, range) => 
          `${range[0]}-${range[1]} из ${total} заявок`,
        pageSizeOptions: ['10', '20', '50', '100'],
        onChange: (page, pageSize) => {
          handleFiltersChange('page', page);
          if (pageSize !== filters.limit) {
            handleFiltersChange('limit', pageSize);
          }
        }
      }}
    />
  );

  const columns: ColumnsType<Application> = [
    {
      title: 'Внутренний номер',
      dataIndex: 'internalNumber',
      key: 'internalNumber',
      render: (_: any, record: Application) => <ApplicationCell application={record} />
    },
    {
      title: 'Маршрут',
      key: 'route',
      render: (application: Application) => (
        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.45 }}>
          {applicationRouteDirectionLabel({
            loadingCity: (application as any).loadingCity,
            unloadingCity: (application as any).unloadingCity,
            loadingAddress: (application as any).loadingAddress,
            unloadingAddress: (application as any).unloadingAddress
          })}
        </Text>
      )
    },
    {
      title: 'Клиент',
      key: 'client',
      render: (_: unknown, record: Application) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {record.client?.name?.trim() ? record.client.name : '—'}
        </Text>
      )
    },
    {
      title: 'Внешний номер',
      dataIndex: 'externalNumber',
      key: 'externalNumber',
      render: (_: unknown, record: Application) => <ExternalApplicationCell application={record} />
    },
    {
      title: 'Логист',
      dataIndex: ['responsibleLogist'],
      key: 'responsibleLogist',
      render: (logist: any) => {
        if (!logist) {
          return (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Не назначен
            </Text>
          );
        }
        return (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {formatResponsibleUserLabel(logist)}
          </Text>
        );
      }
    },
    {
      title: 'Основание',
      key: 'applicationBasis',
      render: (_, record: Application) => {
        const basis = (record as any).applicationBasis;
        if (!basis) {
          return <Text type="secondary">—</Text>;
        }
        const basisLabels: Record<string, string> = {
          'PARTNER_CONTRACT': 'Договор с партнёром',
          'CLIENT_CONTRACT': 'По договору',
          'CLIENT_CONTRACT_REQUEST': 'Договор-заявка'
        };
        const label = basisLabels[basis] || basis;
        return (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {label}
          </Text>
        );
      }
    },
    {
      title: 'Договор',
      key: 'contract',
      render: (_, record: Application) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatWorkspaceCarrierAgreementLine(record.contract ?? null)}
        </Text>
      )
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => <StatusCell status={status} />
    },
    {
      title: 'План погрузки',
      dataIndex: 'plannedLoadingDate',
      key: 'plannedLoadingDate',
      render: (date: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {date ? dayjs(date).format('DD.MM.YYYY') : '—'}
        </Text>
      ),
    },
    {
      title: 'Факт погрузки',
      dataIndex: 'actualLoadingDate',
      key: 'actualLoadingDate',
      render: (date: string | null | undefined) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {date ? dayjs(date).format('DD.MM.YYYY') : '—'}
        </Text>
      ),
    },
    {
      title: 'План выгрузки',
      dataIndex: 'plannedUnloadingDate',
      key: 'plannedUnloadingDate',
      render: (date: string) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {date ? dayjs(date).format('DD.MM.YYYY') : '—'}
        </Text>
      ),
    },
    {
      title: 'Факт выгрузки',
      dataIndex: 'actualUnloadingDate',
      key: 'actualUnloadingDate',
      render: (date: string | null | undefined) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {date ? dayjs(date).format('DD.MM.YYYY') : '—'}
        </Text>
      ),
    },
    {
      title: 'Ставка',
      dataIndex: 'totalCostWithVAT',
      key: 'totalCostWithVAT',
      render: (cost: number) =>
        cost ? (
          <Text style={{ fontSize: 15, fontWeight: 600 }}>
            <MoneyCell value={cost} type="income" />
          </Text>
        ) : (
          <Text type="secondary" style={{ fontSize: 12 }}>
            —
          </Text>
        ),
      sorter: (a: any, b: any) => (a.totalCostWithVAT || 0) - (b.totalCostWithVAT || 0)
    },
    {
      title: 'Грузы',
      dataIndex: 'cargo',
      key: 'cargo',
      render: (cargo: any[]) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {cargo?.length || 0} шт.
        </Text>
      )
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 88,
      fixed: 'right',
      render: (_, record: Application) => (
        <Space size="small" onClick={(e) => e.stopPropagation()}>
          <Tooltip title="Редактировать">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onEditClick(record);
              }}
              size="small"
            />
          </Tooltip>
          <Tooltip
            title={canCancelApplication(record) ? 'Отменить заявку' : 'Заявка уже отменена'}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              disabled={!canCancelApplication(record)}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(record);
              }}
              size="small"
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card style={{ marginBottom: '24px', backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} md={8}>
            <Segmented
              value={listScope}
              onChange={(value) => {
                setListScope(value as ApplicationListScope);
                setFilters((prev) => ({ ...prev, page: 1, status: '' }));
              }}
              options={[
                { label: 'Активные', value: 'active' },
                { label: 'Архив', value: 'archive' },
              ]}
              block
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Поиск (номер/клиент/город)"
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setFilters((prev) => ({ ...prev, page: 1 }));
              }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Input
              placeholder="Направление (откуда/куда)"
              value={filters.direction}
              onChange={(e) => handleFiltersChange('direction', e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Поле периода"
              value={filters.dateField}
              onChange={(value) => handleFiltersChange('dateField', value)}
              style={{ width: '100%' }}
            >
              {(Object.keys(dateFieldLabels) as ApplicationDateField[]).map((key) => (
                <Option key={key} value={key}>{dateFieldLabels[key]}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <RangePicker
              placeholder={['Период от', 'Период до']}
              value={filters.dateRange}
              onChange={(dates) => handleFiltersChange('dateRange', dates)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Клиент"
              value={filters.clientId || undefined}
              onChange={(value) => handleFiltersChange('clientId', value ?? '')}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {(clientsData || []).map((c) => (
                <Option key={c.id} value={c.id.toString()}>{c.name}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Логист"
              value={filters.responsibleLogistId || undefined}
              onChange={(value) => handleFiltersChange('responsibleLogistId', value ?? '')}
              style={{ width: '100%' }}
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {filterResponsibleEligibleUsers(usersData).map((u: any) => {
                const label = formatResponsibleUserLabel(u);
                return (
                  <Option key={u.id} value={String(u.id)} label={label}>
                    {label}
                  </Option>
                );
              })}
            </Select>
          </Col>
          {listScope === 'archive' && (
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Статус"
                value={filters.status || undefined}
                onChange={(value) => handleFiltersChange('status', value ?? '')}
                style={{ width: '100%' }}
                allowClear
              >
                <Option value={ApplicationStatus.COMPLETED}>Завершена</Option>
                <Option value={ApplicationStatus.CANCELLED}>Отменена</Option>
              </Select>
            </Col>
          )}
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="Источник номера"
              value={filters.source || undefined}
              onChange={(value) => handleFiltersChange('source', value ?? '')}
              style={{ width: '100%' }}
              allowClear
            >
              <Option value="internal">Внутренний</Option>
              <Option value="external">Внешний</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button icon={<FilterOutlined />} onClick={resetFilters}>
              Сбросить
            </Button>
          </Col>
        </Row>
        <div style={{ marginTop: 12, color: 'var(--text-secondary)', fontSize: 13 }}>
          Период применяется к полю «{dateFieldLabels[filters.dateField]}». Фактические даты агрегируются по связанным маршрутам (MIN погрузка / MAX выгрузка).
        </div>
      </Card>

      {/* Таблица */}
      <Card style={{ backgroundColor: 'var(--card)', borderColor: 'var(--card-border)' }}>
        {renderTable()}
      </Card>
    </div>
  );
};

export default ApplicationList; 